import React, { ReactElement, useMemo, useState } from 'react';

import { getClassNames, isImage, isVideo } from '../../../../../utils/util';

import { FileType } from '../../../../../types/common.types';

import MediaCarousel from '../../../../common/media-carousel/MediaCarousel';
import Image from '../../../../common/image/Image';
import Video from '../../../../common/video/Video';

import { ReactComponent as CloseIcon } from '../../../../../resources/icons/close.svg';
import { ReactComponent as PlayIcon } from '../../../../../resources/icons/play.svg';

import './tribe-thumbnails.scss';

type Props = {
  files: FileType[];
  deleteFile?: (value: FileType) => void;
  post?: boolean;
  comment?: boolean;
};

const TribeThumbnails = ({ files, deleteFile, post = false, comment = false }: Props): ReactElement | null => {
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | undefined>(undefined);

  const mediaFiles: FileType[] = useMemo((): FileType[] => {
    if (!files) return [];

    return files.filter((file: FileType): boolean => isImage(file.name) || isVideo(file.name));
  }, [files]);

  if (mediaFiles.length === 0) return null;

  return (
    <>
      <div className="tribe-thumbnails" data-testid="tribe-thumbnails">
        {mediaFiles.map((mediaFile: FileType, index: number): ReactElement => {
          if (index > 3) {
            return <React.Fragment key={index} />;
          }

          return (
            <div className={getClassNames('tribe-thumbnails__thumbnail', { comment, post })} key={index}>
              {isImage(mediaFile.name) ? (
                <Image
                  className="tribe-thumbnails__thumbnail__image"
                  src={mediaFile instanceof File ? mediaFile : (mediaFile.documentExternalReference as string)}
                  alt="thumbnail"
                  onClick={(): void => setSelectedImageIndex(index)}
                  testId={`img-${index}`}
                />
              ) : (
                <div className="tribe-thumbnails__thumbnail__video" onClick={(): void => setSelectedImageIndex(index)}>
                  <Video src={mediaFile instanceof File ? mediaFile : mediaFile.link} />
                  <PlayIcon className="tribe-thumbnails__thumbnail__play" />
                </div>
              )}
              {index === 3 && mediaFiles.length > 4 && (
                <div className="tribe-thumbnails__thumbnail__cover" onClick={(): void => setSelectedImageIndex(3)}>
                  <p>+{mediaFiles.length - 4}</p>
                </div>
              )}
              {index < 4 && deleteFile && (
                <CloseIcon
                  className="tribe-thumbnails__delete-icon"
                  onClick={(): void => deleteFile(mediaFile)}
                  data-testid="tribe-thumbnails-delete-icon"
                />
              )}
            </div>
          );
        })}
      </div>

      {selectedImageIndex !== undefined && (
        <MediaCarousel mediaFiles={mediaFiles} startIndex={selectedImageIndex} setStartIndex={setSelectedImageIndex} />
      )}
    </>
  );
};

export default TribeThumbnails;
